import { useCallback, useEffect, useMemo, useState } from "react";
import api from "../lib/api";
import { useAuth } from "../lib/auth";
import { toast } from "sonner";
import { Wrench, Plus, Trash2, Search, Phone, MessageSquare, Mail as MailIcon, X, Pencil, MapPin } from "lucide-react";

const initialForm = {
  name: "", vendor_type: "Electrician", country: "India", city: "",
  area: "", contact_person: "", phone: "", whatsapp: "",
  email: "", address: "", rate_notes: "", notes: "",
};

function VendorForm({ initial, types, onSubmit, onCancel, busy }) {
  const [f, setF] = useState(initial);
  const set = (k, v) => setF(s => ({ ...s, [k]: v }));
  const submit = () => {
    if (!f.name.trim() || !f.phone.trim() || !f.country.trim() || !f.city.trim()) {
      toast.error("Name, phone, country and city are required"); return;
    }
    onSubmit(f);
  };
  return (
    <div className="grid grid-cols-1 sm:grid-cols-3 gap-2" data-testid="vendor-form">
      <input className="input-aii" placeholder="Vendor name *" value={f.name} onChange={e => set("name", e.target.value)}/>
      <select className="input-aii" value={f.vendor_type} onChange={e => set("vendor_type", e.target.value)}>
        {types.map(t => <option key={t} value={t}>{t}</option>)}
      </select>
      <input className="input-aii" placeholder="Contact person" value={f.contact_person} onChange={e => set("contact_person", e.target.value)}/>
      <input className="input-aii" placeholder="Country *" value={f.country} onChange={e => set("country", e.target.value)}/>
      <input className="input-aii" placeholder="City *" value={f.city} onChange={e => set("city", e.target.value)}/>
      <input className="input-aii" placeholder="Area / neighbourhood" value={f.area} onChange={e => set("area", e.target.value)}/>
      <input className="input-aii" placeholder="Phone *" value={f.phone} onChange={e => set("phone", e.target.value)}/>
      <input className="input-aii" placeholder="WhatsApp" value={f.whatsapp} onChange={e => set("whatsapp", e.target.value)}/>
      <input className="input-aii" placeholder="Email" value={f.email} onChange={e => set("email", e.target.value)}/>
      <input className="input-aii sm:col-span-2" placeholder="Address" value={f.address} onChange={e => set("address", e.target.value)}/>
      <input className="input-aii" placeholder="Rate / rate notes" value={f.rate_notes} onChange={e => set("rate_notes", e.target.value)}/>
      <input className="input-aii sm:col-span-3" placeholder="Notes" value={f.notes} onChange={e => set("notes", e.target.value)}/>
      <div className="sm:col-span-3 flex gap-2 justify-end">
        {onCancel && <button onClick={onCancel} className="px-3 py-2 text-sm border border-[color:var(--aii-border)] rounded-md">Cancel</button>}
        <button onClick={submit} disabled={busy} data-testid="vendor-submit" className="px-4 py-2 bg-brand text-white rounded-md font-semibold hover:bg-brand-dark disabled:opacity-50 flex items-center gap-1.5"><Plus size={14}/> Save</button>
      </div>
    </div>
  );
}

export default function Vendors() {
  const { user } = useAuth();
  const [rows, setRows] = useState([]);
  const [types, setTypes] = useState([]);
  const [filters, setFilters] = useState({ country: "", city: "", vendor_type: "", q: "" });
  const [busy, setBusy] = useState(false);
  const [editing, setEditing] = useState(null);
  const [adding, setAdding] = useState(false);

  const canManage = ["FOUNDER", "BDOM", "GEHM"].includes(user.real_role);

  const load = useCallback(async () => {
    const q = new URLSearchParams(Object.entries(filters).filter(([, v]) => v)).toString();
    const [r, t] = await Promise.all([
      api.get(`/vendors${q ? `?${q}` : ""}`),
      api.get("/vendors/types").catch(() => ({ data: { types: [] } })),
    ]);
    setRows(r.data || []);
    setTypes(t.data.types || []);
  }, [filters]);
  useEffect(() => { load(); }, [load]);

  const add = async (f) => {
    setBusy(true);
    try { await api.post("/vendors", f); toast.success("Vendor added"); setAdding(false); await load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };
  const patch = async (id, updates) => {
    setBusy(true);
    try { await api.patch(`/vendors/${id}`, updates); toast.success("Updated"); setEditing(null); await load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };
  const del = async (v) => {
    if (!window.confirm(`Remove ${v.name}?`)) return;
    try { await api.delete(`/vendors/${v.id}`); toast.success("Removed"); await load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const grouped = useMemo(() => {
    const g = {};
    for (const r of rows) {
      const k = `${r.country} · ${r.city}`;
      (g[k] = g[k] || []).push(r);
    }
    return g;
  }, [rows]);

  return (
    <div className="space-y-5" data-testid="vendors-page">
      <div>
        <div className="text-xs uppercase tracking-widest text-[color:var(--aii-muted)]">Local support network</div>
        <h1 className="text-2xl font-semibold flex items-center gap-2"><Wrench className="text-brand"/> Vendors registry</h1>
        <p className="text-sm text-[color:var(--aii-text-2)] mt-1">Electricians · Plumbers · Carpenters · Ambulance · Taxi — everything the ground team needs. Filter by country and city.</p>
      </div>

      <div className="aii-card p-3 grid grid-cols-2 sm:grid-cols-4 gap-2">
        <input className="input-aii" placeholder="Country" value={filters.country} onChange={e => setFilters({...filters, country: e.target.value})} data-testid="vf-country"/>
        <input className="input-aii" placeholder="City" value={filters.city} onChange={e => setFilters({...filters, city: e.target.value})} data-testid="vf-city"/>
        <select className="input-aii" value={filters.vendor_type} onChange={e => setFilters({...filters, vendor_type: e.target.value})} data-testid="vf-type">
          <option value="">All types</option>
          {types.map(t => <option key={t} value={t}>{t}</option>)}
        </select>
        <div className="flex items-center gap-1">
          <Search size={14} className="text-[color:var(--aii-muted)] absolute translate-x-2"/>
          <input className="input-aii pl-8" placeholder="Search name / phone" value={filters.q} onChange={e => setFilters({...filters, q: e.target.value})}/>
        </div>
      </div>

      {canManage && (
        <div className="aii-card p-5">
          {!adding ? (
            <button onClick={() => setAdding(true)} data-testid="vendor-add" className="text-sm px-4 py-2 bg-brand text-white rounded-md hover:bg-brand-dark flex items-center gap-1.5"><Plus size={14}/> Add vendor</button>
          ) : (
            <VendorForm initial={initialForm} types={types} busy={busy} onSubmit={add} onCancel={() => setAdding(false)}/>
          )}
        </div>
      )}

      {Object.keys(grouped).length === 0 && (
        <div className="aii-card p-8 text-center text-sm text-[color:var(--aii-muted)]">No vendors match your filters.</div>
      )}
      {Object.entries(grouped).map(([loc, vs]) => (
        <div key={loc}>
          <div className="flex items-center gap-2 mb-2 mt-3">
            <MapPin size={14} className="text-brand"/>
            <h3 className="font-display font-semibold text-base">{loc}</h3>
            <span className="text-xs text-[color:var(--aii-muted)]">({vs.length})</span>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3">
            {vs.map(v => (
              <div key={v.id} data-testid={`vendor-${v.id}`} className="aii-card p-4">
                {editing === v.id ? (
                  <VendorForm initial={v} types={types} busy={busy} onSubmit={(f) => patch(v.id, f)} onCancel={() => setEditing(null)}/>
                ) : (
                  <>
                    <div className="flex items-start justify-between gap-2">
                      <div>
                        <div className="text-[10px] uppercase tracking-widest text-brand font-bold">{v.vendor_type}</div>
                        <div className="font-display font-semibold text-base">{v.name}</div>
                        {v.contact_person && <div className="text-xs text-[color:var(--aii-muted)]">{v.contact_person}</div>}
                      </div>
                      {canManage && (
                        <div className="flex items-center gap-1">
                          <button onClick={() => setEditing(v.id)} className="text-[color:var(--aii-muted)] hover:text-brand p-1"><Pencil size={13}/></button>
                          <button onClick={() => del(v)} className="text-[color:var(--aii-muted)] hover:text-red-600 p-1"><Trash2 size={13}/></button>
                        </div>
                      )}
                    </div>
                    <div className="mt-2 space-y-1 text-sm">
                      {v.phone && <a href={`tel:${v.phone}`} className="flex items-center gap-1.5 text-brand hover:underline"><Phone size={12}/> {v.phone}</a>}
                      {v.whatsapp && <a href={`https://wa.me/${v.whatsapp.replace(/[^\d]/g, "")}`} target="_blank" rel="noopener noreferrer" className="flex items-center gap-1.5 text-emerald-700 hover:underline"><MessageSquare size={12}/> WhatsApp</a>}
                      {v.email && <a href={`mailto:${v.email}`} className="flex items-center gap-1.5 text-slate-700 hover:underline"><MailIcon size={12}/> {v.email}</a>}
                    </div>
                    {(v.area || v.address) && <div className="mt-2 text-xs text-[color:var(--aii-muted)]">{[v.area, v.address].filter(Boolean).join(" · ")}</div>}
                    {v.rate_notes && <div className="mt-2 text-xs bg-[color:var(--aii-green-tint)] text-[color:var(--aii-green-dark)] px-2 py-1 rounded inline-block">₹ {v.rate_notes}</div>}
                    {v.notes && <div className="mt-2 text-xs text-[color:var(--aii-text-2)]">{v.notes}</div>}
                  </>
                )}
              </div>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}
